// src/components/assistant/ChatbotPrepareDialog.js
// Copy-paste path for any chatbot: copy the prompt, paste the JSON answer back.
import React, { useEffect, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { getChatbotPrompt, importStep } from "../../api";
import { copyText } from "../../utils/clipboard";
import { friendlyError } from "../../utils/errors";

export default function ChatbotPrepareDialog({ open, step, onClose, onImported }) {
  const [prompt, setPrompt] = useState("");
  const [answer, setAnswer] = useState("");
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !step) return;
    setError(null);
    setCopied(false);
    setAnswer("");
    getChatbotPrompt(step).then((r) => setPrompt(r.prompt)).catch((e) => setError({ message: friendlyError(e) }));
  }, [open, step]);

  const doImport = async () => {
    setBusy(true);
    setError(null);
    try {
      const view = await importStep(step, answer);
      onImported?.(step, view);
      onClose();
    } catch (err) {
      setError({ message: err.message, fields: err.fieldErrors });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="chatbot-title">
      <DialogTitle id="chatbot-title" sx={{ fontWeight: 700 }}>Prepare step {step} with any AI chatbot</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2">
            <strong>1.</strong> Copy this prompt and paste it into any chatbot. Answer its questions.
          </Typography>
          {prompt ? (
            <Box>
              <TextField value={prompt} multiline maxRows={8} fullWidth size="small" InputProps={{ readOnly: true }} label="Prompt" />
              <Button sx={{ mt: 1 }} startIcon={<ContentCopyIcon />} onClick={async () => setCopied(await copyText(prompt))}>
                {copied ? "Copied" : "Copy prompt"}
              </Button>
            </Box>
          ) : !error && <CircularProgress size={20} />}
          <Typography variant="body2">
            <strong>2.</strong> Paste the chatbot's final answer (the JSON block) here. It will be filled in as a proposal that you review and confirm.
          </Typography>
          <TextField
            label="Chatbot answer"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            multiline
            minRows={5}
            maxRows={14}
            fullWidth
            inputProps={{ maxLength: 40000 }}
          />
          {error && (
            <Alert severity="error" role="alert">
              {error.message}
              {error.fields && (
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {Object.entries(error.fields).map(([f, m]) => <li key={f}><strong>{f.replace(/_/g, " ")}</strong>: {m}</li>)}
                </Box>
              )}
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary">
            What the chatbot sees is governed by that chatbot's terms, not K8's.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button variant="contained" onClick={doImport} disabled={busy || !answer.trim()}>Import answer</Button>
      </DialogActions>
    </Dialog>
  );
}
